import { DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, Label, Panel, RepeatItemDisplayEvent, Image, ChangeEvent, ClickEvent, Button, Textbox, Component, DataSourceRemoteDataChangeEvent, DropdownItem, DataSourceMode, KeyHandler, KeyEvent, Snackbar, Checkbox } from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutMobileCommChat } from "./autogen/AutogenLayoutMobileCommChat";
import { RepeatItem } from "@mcleod/components/src/components/repeater/RepeatItem";
import {
    Api, DatePart, DateUtil, DebouncedFunction, DisplayType, DOMUtil, Keys, ModelRow, StringUtil, Styles
} from "@mcleod/core";
import { CommonTooltips } from "@mcleod/common";

const HighlightStyles = Styles.make("chat-highlight", () => {
    return {
        selectedHighlight: { backgroundColor: "yellow !important", fontWeight: "bold" }
    };
});

const searchBoxStyles = { borderWidth: 0, rowBreak: false, captionVisible: false, imagePostName: ImageName.magnifyingGlass, placeholder: "Search" };
const navButtonStyles = { borderWidth: 0, rowBreak: false, fontSize: "large", imageName: ImageName.chevron, imageWidth: 24, padding: 0 };

class Highlighter {
    private parentElement: HTMLElement;
    private _data: ModelRow[] = [];
    private _components: Component[];
    private _highlightedElements: HTMLElement[] = []; // viewable highlight elements
    private _highlightIndexes = []; // index of all rows that have matched highlights
    private highlightCount = 0;
    private _currentHighlightIndex = 0;
    private debouncedHighlight: (value: string) => void = DebouncedFunction.create((value) => this.highlightText(value), 200);
    private searchPanel: Panel;
    private getElement: (item: Component) => HTMLElement;
    private getText: (row: ModelRow) => string;
    public onItemDisplay: (item: Component) => void;

    // Search Components
    private searchBox = new Textbox();
    private labelCurrentIndex = new Label();
    private labelOf = new Label();
    private labelTotal = new Label();
    private panelSearchNav = new Panel();
    private buttonDown = new Button();
    private buttonUp = new Button();

    constructor(parentElement: HTMLElement, searchPanel: Panel, getElement: (item: Component)=>HTMLElement, getText: (row: ModelRow) => string) {
        this.parentElement = parentElement;
        this.searchPanel = searchPanel;
        this.onItemDisplay = (item: Component) => this.displayItem(item);
        this.getText = (row: ModelRow) => getText(row);
        this.getElement = (item: Component) => getElement(item);
        this.buildSearchComponents();
        this.addComponentListeners();
    }

    displayItem(item: Component) {
        this.highlightItem(item);
        if (this.highlightIndexes.length > 0) {
            const lastMarkedIndex = this.highlightIndexes.at(-1);
            if (this.components[lastMarkedIndex] == item) {
                this.countHighlightTotal();
                this.focusHighlight(this.highlightedElements[this.currentHighlightIndex]);
            }
        }
    }

    resetHighlighted() {
        this.currentHighlightIndex = 0;
        this.highlightedElements = [];
    }

    highlightText(value: string) {
        this.resetHighlighted();
        this.components.forEach((item: Component) => {
            this.highlightRow(value, item);
        });
        this.countHighlightTotal();
        if (this.highlightedElements.length > 0) {
            this.focusHighlight(this.highlightedElements[0]);
        }
        if (this.highlightIndexes.length > 0){
            this.scrollToHighlight(0, this.getFirstValidHighlight(0));
        }
    }

    highlightItem(item: Component) {
        this.highlightRow(this.searchBox.text, item);
    }

    highlightRow(value: string, item: Component) {
        const highlightedElement = this.getElement(item);
        if (highlightedElement != null) {
            highlightedElement.style.flexDirection = "column";
            highlightedElement.style.alignItems = "flex-start";
            let text = highlightedElement.innerHTML;

            // Remove any existing <span> and <mark> elements
            text = text.replace(/<\/?(mark|span)(\s+[^>]*)?>/g, '');
            highlightedElement.innerHTML = text;

            if (value.trim() !== "") {
                // Check if the entire text matches the regex
                const regex = new RegExp(`(${value})`, "gi");
                if (regex.test(text)) {
                    // Wrap the entire text in a <span> tag
                    text = `<span>${text}</span>`;
                    highlightedElement.innerHTML = text;

                    // apply the highlighting logic
                    const spanElements = highlightedElement.querySelectorAll('span');

                    spanElements.forEach(span => {
                        let text = span.innerHTML;
                        // Use regex to wrap the search term in a <mark> tag (case-insensitive)
                        const regex = new RegExp(`(${value})`, "gi");
                        text = text.replace(regex, '<mark style="background-color: #FFFFC8">$1</mark>');

                        span.innerHTML = text;
                    });

                    if (highlightedElement.innerHTML.includes("<mark")) {
                        const markedElements = Array.from(highlightedElement.getElementsByTagName("mark"));
                        this.highlightedElements.push(...markedElements);
                    }
                }
            }
        }
    }

    countHighlightTotal() {
        this.highlightIndexes = [];
        this.data.forEach((row: ModelRow, index: number) => {
            const message = this.getText(row);
            if (!StringUtil.isEmptyString(this.searchBox.text)) {
                const regex = new RegExp(`(${this.searchBox.text})`, "gi");
                const matches = message.match(regex);
                if (matches != null) {
                    matches.forEach(match => this.highlightIndexes.push(index));
                }
            }
        });
        this.panelSearchNav.visible = this.highlightIndexes.length > 0;
        this.labelTotal.text = this.highlightIndexes.length.toString();
        this.highlightCount = this.highlightIndexes.length;
    }

    buttonDownOnClick(event: ClickEvent) {
        this.navigateHighlight(-1);
    }

    buttonUpOnClick(event: ClickEvent) {
        this.navigateHighlight(1);
    }

    navigateHighlight(direction: number) {
        let newElement: HTMLElement;
        const newIndex = this.highlightedElements.length == 0 ? 0 : (this.currentHighlightIndex + direction + this.highlightCount) % this.highlightCount;
        if (this.highlightedElements.length > 1) {
            const prevElement = this.highlightedElements[this.currentHighlightIndex];
            if (prevElement != null)
                this.clearHighlight(prevElement);
        }

        if (newIndex > this.highlightedElements.length-1 || this.highlightedElements.length == 0) {
            newElement = this.components[this.highlightIndexes[newIndex]].element;
        } else {
            newElement = this.highlightedElements[newIndex];
            this.focusHighlight(newElement);
        }

        this.scrollToHighlight(newIndex, newElement);
        this.currentHighlightIndex = newIndex;
    }

    getFirstValidHighlight(index: number): HTMLElement {
        return this.highlightedElements[index] ?? this.components[this.highlightIndexes[index]].element;
    }

    scrollToHighlight(index: number, element: HTMLElement) {
        const percent = DOMUtil.getPercentVerticallyVisible(element, this.parentElement);
        if (percent < 10) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }

    clearHighlight(element: HTMLElement) {
        element.classList.remove(HighlightStyles.selectedHighlight);
    }

    focusHighlight(element: HTMLElement) {
        element.classList.add(HighlightStyles.selectedHighlight);
    }

    buildSearchComponents() {
        this.searchBox.setProps(searchBoxStyles);
        this.labelTotal.setProps({ fontBold: true, rowBreak: false });
        this.labelOf.setProps({ fontBold: true, rowBreak: false, paddingRight: 0, paddingLeft: 0, text: "of" });
        this.labelCurrentIndex.setProps({ fontBold: true, rowBreak: false });
        this.panelSearchNav.setProps({ padding: 0, visible: false });
        this.buttonDown.setProps(navButtonStyles);
        this.buttonUp.setProps({ ...navButtonStyles, imageRotation: 180 });

        this.panelSearchNav.add(this.labelCurrentIndex, this.labelOf, this.labelTotal, this.buttonDown, this.buttonUp);
        this.searchPanel.add(this.searchBox, this.panelSearchNav);
    }

    addComponentListeners() {
        this.searchBox.addChangeListener((event: ChangeEvent) => this.debouncedHighlight(event.newValue));
        this.buttonDown.addClickListener((event: ClickEvent) => this.buttonDownOnClick(event));
        this.buttonUp.addClickListener((event: ClickEvent) => this.buttonUpOnClick(event));
    }

    set data(value: ModelRow[]) {
        this._data = value;
    }

    get data() {
        return this._data;
    }

    set components(value: Component[]) {
        this._components = value;
    }

    get components() {
        return this._components;
    }

    set highlightedElements(value: HTMLElement[]) {
        this._highlightedElements = value;
    }

    get highlightedElements() {
        return this._highlightedElements;
    }

    set highlightIndexes(value: number[]) {
        this._highlightIndexes = value;
    }

    get highlightIndexes() {
        return this._highlightIndexes;
    }

    set currentHighlightIndex(value: number) {
        this._currentHighlightIndex = value;
        this.labelCurrentIndex.text = (value+1).toString();
    }

    get currentHighlightIndex() {
        return this._currentHighlightIndex;
    }
}

export class MobileCommChat extends AutogenLayoutMobileCommChat {
    private allData: ModelRow[] = [];
    private filteredButton: Button;
    private outboundDate = null;
    private inboundDate = null;
    private highlighter: Highlighter;
    private enterKeyHandler: KeyHandler = null;
    private confirmAll: boolean = false;
    private debouncedSend: () => void = DebouncedFunction.create(() => this.send(), 200);

    async onLoad() {
        this.repeaterChats.element.style.flexDirection = "column-reverse";
        this.panelToolbar.element.style.boxShadow = "0px -5px 5px rgba(0,0,0,.2)";
        this.highlighter = new Highlighter(this.repeaterChats.element, this.panelSearchBox, this.getElementForHighlight.bind(this), this.getHighlightText);
        this.enterKeyHandler = this.createKeyHandler(Keys.ENTER, this.onEnter.bind(this), null, this.element);
        this.addKeyHandler(this.enterKeyHandler);
    }

    onEnter(event: KeyEvent) {
        if (document.activeElement == this.textboxMessage._input) {
            this.buttonSend.clicked();
        }
    }

    sourceMcMessageOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        event.preventDefault();
        const change = event.change;
        const changeRow = change.data as ModelRow;
        const index = this.sourceMcMessage.findRowInData(changeRow);
        const currentRow = index >= 0 ? this.sourceMcMessage.data[index] : null;

        if (!this.sourceMcMessage.lastSearch.data.mc_unit_no.includes(changeRow.get("mc_unit_no"))) {
            return;
        }

        if (!currentRow?.isNull("lastUpdateNanos") && currentRow?.get("lastUpdateNanos", null) > event.change.lastUpdateNanos) {
            return;
        }
        changeRow.set("lastUpdateNanos", event.change.lastUpdateNanos);

        const statusChanged = index > 0 && changeRow.get("msg_sending_status") != this.sourceMcMessage.data[index].get("msg_sending_status");
        if (change.type == "ADD" || change.type == "UPDATE") {
            if (index == -1) {
                this.sourceMcMessage.data.unshift(changeRow);
            } else {
                this.sourceMcMessage.data[index].setValues(changeRow.data);
            }
        } else if (change.type == "DELETE") {
            if (index >= 0) {
                this.sourceMcMessage.data.splice(index, 1);
            }
        }
        if (statusChanged) {
            const repeatItem = this.repeaterChats.components[index] as RepeatItem;
            repeatItem.boundRow = this.sourceMcMessage.data[index];
            this.checkForUnconfirmed(repeatItem);
            this.updateConfirmedState();
        } else {
            this.allData = [...this.sourceMcMessage.data];
            let updatedData = this.allData;
            if (this.filteredButton != null) {
                updatedData = this.filterData(this.filteredButton == this.buttonInboundFilter ? "I" : "O");
            }
            this.updateData(updatedData);
        }
        this.checkForMessageTypes();
    }

    sourceMcMessageAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.allData = [...this.sourceMcMessage.data];
            this.highlighter.components = this.repeaterChats.components;
            this.highlighter.data = this.allData;
            this.populateMcUnitDropdown();
            this.checkForMessageTypes();
        }
    }

    sourceMovementEquipmentAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.setStatusIcon(this.imageStatus, this.sourceMovementEquipment.activeRow.get("status"));
        }
    }

    repeaterChatsOnItemDisplay(event: RepeatItemDisplayEvent) {
        const item = event.target as RepeatItem;
        item.minHeight = null;
        this.determineMessageType(item);
        this.showDateDivider(item);
        this.checkForUnconfirmed(item);
        this.highlighter.onItemDisplay(item);
    }

    checkForMessageTypes() {
        const hasInbound = this.repeaterChats.data.some((row: ModelRow) => row.get("message_type") == "I" && row.get("msg_sending_status") != 200);
        this.checkboxConfirmedSelectAll.visible = this.buttonConfirm.visible = hasInbound;
        this.buttonConfirm.enabled = false; // reset confirmed button state to false
    }

    checkboxConfirmedSelectAllOnChange(event: ChangeEvent) {
        this.checkboxConfirmedSelectAll.caption = event.newValue ? "Deselect All" : "Select All";
        this.repeaterChats.components.forEach((item: RepeatItem) => {
            const checkboxConfirmed = this.getCheckboxConfirmed(item as RepeatItem);
            if (item.data.get("message_type") == "I" && checkboxConfirmed) {
                this.confirmAll = event.newValue;
                checkboxConfirmed.checked = event.newValue;
            }
        });
    }

    async buttonConfirmOnClick(event: ClickEvent) {
        const promises: Promise<any>[] = [];

        for (const item of this.repeaterChats.components) {
            const modelRow = (item as RepeatItem).data;
            const checkboxConfirmed = this.getCheckboxConfirmed(item as RepeatItem);
            if (checkboxConfirmed && checkboxConfirmed.checked) {
                promises.push(
                    Api.post("lme/dispatch/mobilecomm/confirm-message", {
                        mc_message_id: modelRow.get("id")
                    })
                );
            }
        }

        try {
            await Promise.all(promises);
        } catch (error) {
            console.error("Error confirming messages:", error);
        }
    }

    getCheckboxConfirmed(item: RepeatItem): Checkbox {
        const checkboxField = item.data.get("mc_form_number") == 0 ? "checkboxConfirmed" : "checkboxFormConfirmed";
        return item.findComponentById(checkboxField) as Checkbox;
    }

    checkForUnconfirmed(item: RepeatItem) {
        const data = item.data;
        if (data.get("message_type") != "I")
            return;
        if (data.get("mc_form_number") == 0) {
            this.setFreeformConfirmedStyling(item);
        } else {
            this.setFormConfirmedStyling(item);
        }
    }

    setFreeformConfirmedStyling(item: RepeatItem) {
        const data = item.data;
        const unconfirmed = data.get("msg_sending_status") != 200;
        const panelInboundText = item.findComponentById("panelInboundText") as Panel;
        const checkboxConfirmed = item.findComponentById("checkboxConfirmed") as Checkbox;
        const labelUnconfirmed = item.findComponentById("labelUnconfirmed") as Label;
        if (checkboxConfirmed) {
            checkboxConfirmed.checked = this.confirmAll;
            checkboxConfirmed.visible = unconfirmed;
        }
        if (labelUnconfirmed)
            labelUnconfirmed.visible = unconfirmed;
        if (panelInboundText) {
            panelInboundText.setProps({
                backgroundColor: unconfirmed ? "rgba(184, 209, 168, 0.25)" : "default.reverse",
                borderColor: unconfirmed ? "success" : "subtle.light",
                color: unconfirmed ? "success" : "default",
                fontBold: unconfirmed,
            });
        }
    }

    setFormConfirmedStyling(item: RepeatItem) {
        const data = item.data;
        const unconfirmed = data.get("msg_sending_status") != 200;
        const panelFormInfo = item.findComponentById("panelFormInfo") as Panel;
        const checkboxFormConfirmed = item.findComponentById("checkboxFormConfirmed") as Checkbox;
        const panelFormConfirmed = item.findComponentById("panelFormConfirmed") as Label;
        if (checkboxFormConfirmed) {
            checkboxFormConfirmed.checked = this.confirmAll;
            checkboxFormConfirmed.visible = unconfirmed;
        }
        if (panelFormConfirmed)
            panelFormConfirmed.visible = unconfirmed;
        if (panelFormInfo) {
            panelFormInfo.setProps({
                backgroundColor: unconfirmed ? "rgba(184, 209, 168, 0.25)" : "subtle",
                borderColor: unconfirmed ? "success" : "subtle",
                color: unconfirmed ? "success" : "default.reverse",
                fontBold: unconfirmed,
                borderWidth: unconfirmed ? 1 : 0,
            });
        }
    }

    checkboxConfirmedOnChange(event: ChangeEvent) {
        this.updateConfirmedState();
    }

    updateConfirmedState() {
        let hasChecked = false;
        let allChecked = true;
        for (const item of this.repeaterChats.components) {
            if ((item as RepeatItem).data.get("message_type") == "I") {
                const checkboxConfirmed = this.getCheckboxConfirmed(item as RepeatItem);
                if (checkboxConfirmed && checkboxConfirmed.visible) {
                    if (!checkboxConfirmed.checked) {
                        allChecked = false;
                    } else {
                        hasChecked = true;
                    }
                }
            }
        }
        this.buttonConfirm.enabled = hasChecked;
        if (!hasChecked && this.checkboxConfirmedSelectAll.checked) {
            this.checkboxConfirmedSelectAll.checked = false;
        } else if (!this.checkboxConfirmedSelectAll.checked && allChecked) {
            this.checkboxConfirmedSelectAll.checked = true;
        }
    }

    populateMcUnitDropdown() {
        const tractorMcUnit = this.sourceMcMessage.summaryData?.get("tractor_mc_unit_no", null);
        const driverMcUnit = this.sourceMcMessage.summaryData?.get("driver_mc_unit_no", null);
        const driver2McUnit = this.sourceMcMessage.summaryData?.get("driver2_mc_unit_no", null);
        if (tractorMcUnit != null || driverMcUnit != null || driver2McUnit != null) {
            this.textboxMcUnitNo.items = [
                ...(driverMcUnit ? [new DropdownItem(driverMcUnit, driverMcUnit)] : []),
                ...(driver2McUnit ? [new DropdownItem(driver2McUnit, driver2McUnit)] : []),
                ...(tractorMcUnit ? [new DropdownItem(tractorMcUnit, tractorMcUnit)] : [])
            ];
            const lastInboundMessage = this.sourceMcMessage.data.find(row => row.get("message_type") == "I");
            const inboundMcUnit = lastInboundMessage?.get("mc_unit_no");
            const lastMcUnitMenuItem = inboundMcUnit
                ? this.textboxMcUnitNo.items.find(item => item.value === inboundMcUnit)
                : null;
            this.textboxMcUnitNo.selectedItem = lastMcUnitMenuItem ?? this.textboxMcUnitNo.items[0];
        } else {
            this.textboxMessage.enabled = this.textboxMcUnitNo.enabled = false;
            this.buttonSendEnabled(false);
            this.buttonSend.disabledTooltip = this.textboxMcUnitNo.disabledTooltip = "There is no tractor or driver mobile comm unit associated with this driver.";
        }
    }

    buttonSendEnabled(enabled: boolean) {
        this.buttonSend.setProps({
            enabled,
            backgroundColor: enabled ? "primary" : "subtle.light",
            color: enabled ? "primary.reverse" : "subtle"
        });
    }

    async buttonSendOnClick(event: ClickEvent) {
        this.debouncedSend();
    }

    send() {
        if (this.sourceMcMessageAdd.activeRow) { // timing could mean activeRow is null if user spams the button
            if (this.sourceMcMessageAdd.activeRow.isNull("message_text")) {
                Snackbar.showWarningSnackbar("A valid message is required to send.");
                return;
            }
            const selectedMcUnit = this.textboxMcUnitNo.selectedItem.value;
            const tractorMcUnit = this.sourceMcMessage.summaryData?.get("tractor_mc_unit_no", null);
            const driverId = tractorMcUnit == selectedMcUnit
                    ? this.sourceMcMessage.lastSearch.data.selected_driver
                    : selectedMcUnit;
            this.sourceMcMessageAdd.activeRow.set({
                ...(this.sourceMovementEquipment.lastSearch ? { movement_id: this.sourceMovementEquipment.lastSearch.data.id } : {}),
                driver_id: driverId,
                mc_unit_no: selectedMcUnit,
                mc_form_number: 0,
                message_send_date: new Date(),
                movement_id: this.sourceMovementEquipment.activeRow.get("currentmovement_id"),
                in_zone_id: this.sourceMovementEquipment.activeRow.get("origin.zone_id"),
                out_zone_id: this.sourceMovementEquipment.activeRow.get("origin.zone_id"),
            });
            try {
                this.buttonSendEnabled(false);
                this.sourceMcMessageAdd.post().then(() => {
                    this.sourceMcMessageAdd.data = [];
                    this.sourceMcMessageAdd.mode = DataSourceMode.ADD;
                });
            } finally {
                this.buttonSendEnabled(true);
            }
        }
    }

    buttonInboundFilterOnClick(event: ClickEvent) {
        this.filter(event.target as Button);
    }

    buttonOutboundFilterOnClick(event: ClickEvent) {
        this.filter(event.target as Button);
    }

    filter(button: Button) {
        let data = this.allData;
        const messageType = button == this.buttonInboundFilter ? "I" : "O";
        this.filteredButton = this.filteredButton == null || this.filteredButton != button ? button : null;
        [this.buttonInboundFilter, this.buttonOutboundFilter].forEach((btn) => {
            if (btn == this.filteredButton) {
                btn.setProps({ backgroundColor: "success", color: "default.reverse" });
                data = this.filterData(messageType);
            } else {
                btn.setProps({ backgroundColor: "white", color: "McLeodTertiary" });
            }
        });
        this.updateData(data);
        this.checkForMessageTypes();
    }

    filterData(messageType: string): ModelRow[] {
        return this.allData.filter((row) => row.get("message_type") == messageType);
    }

    updateData(data: ModelRow[]) {
        this.repeaterChats.data = this.highlighter.data = data;
        this.highlighter.components = this.repeaterChats.components;
        this.highlighter.resetHighlighted();
        this.highlighter.countHighlightTotal();
        this.outboundDate = this.inboundDate = null;
    }

    showDateDivider(item: RepeatItem) {
        const data = item.data;
        const sendDate = data.getDate("message_send_date");
        const nextMsg = this.repeaterChats.getComponent(this.repeaterChats.indexOf(item)+1) as RepeatItem;
        const nextDate = nextMsg?.data.getDate("message_send_date");
        const lastItem = this.repeaterChats.getComponentCount()-1 == this.repeaterChats.indexOf(item);

        // Account for anything on today so date header isn't generated until needed
        if (this.sameDate(nextMsg?.data.getDate("message_send_date"), sendDate) && !lastItem) {
            return;
        }

        if (lastItem || !this.sameDate(nextDate, sendDate)) {
            data.set("date_divider", sendDate);
            const panelDateDivider = item.findComponentById("panelDateDivider") as Panel;
            panelDateDivider.visible = true;
            panelDateDivider.displayData(data, null, 0);
            this.outboundDate = this.inboundDate = null;
        }
    }

    determineMessageType(item: RepeatItem) {
        const data = item.data;
        if (data.get("mc_form_number", 0) != 0) {
            return this.displayFormMessage(item);
        }
        switch (data.get("message_type")) {
            case "I":
                return this.displayInboundMessage(item);
            case "O":
                return this.displayOutboundMessage(item);
        }
    }

    displayFormMessage(item: RepeatItem): void {
        const panelForm = item.findComponentById("panelForm") as Panel;
        panelForm.visible = true;
        const sendDate = item.data.getDate("message_send_date");
        if (this.isToday(sendDate)) {
            const labelFormTime = item.findComponentById("labelFormTime") as Label;
            this.formatDateToTime(item.data, labelFormTime);
        }
        const panelFormInfo = item.findComponentById("panelFormInfo") as Panel;
        CommonTooltips.setTooltipContentLayout(
          panelFormInfo,
            item.data.get("id"),
            "lme/dispatch/mobilecomm/MobileCommMessageQuickInfo"
        );
        this.inboundDate = this.outboundDate = null;
    }

    displayInboundMessage(item: RepeatItem): void {
        const panelOutbound = item.findComponentById("panelOutbound") as Panel;
        item.remove(panelOutbound);
        const panelInbound = item.findComponentById("panelInbound") as Panel;
        panelInbound.visible = true;
        this.outboundDate = null;
        const sendDate = item.data.getDateTime("message_send_date");
        const nextMsg = this.repeaterChats.getComponent(this.repeaterChats.indexOf(item)+1) as RepeatItem;
        const nextDate = nextMsg?.data.get("message_type") == "I" ? nextMsg?.data.getDateTime("message_send_date") : null;
        const labelInboundTime = item.findComponentById("labelInboundTime") as Label;
        const labelInboundUser = item.findComponentById("labelInboundUser") as Label;
        this.updateDisplayedName(labelInboundUser, item.data.get("mc_unit_no"));

        if (this.isToday(sendDate)) {
            this.formatDateToTime(item.data, labelInboundTime);
        }

        if ((this.inboundDate == null || this.sameDate(this.inboundDate, sendDate) || nextDate == null)
                && !this.withinOneMinute(sendDate, nextDate)) {
            this.inboundDate = sendDate;
        } else {
            if (nextMsg && nextMsg.data.get("mc_user_id") != item.data.get("mc_user_id")) {
                return;
            }
            if (nextMsg && nextMsg.data.get("mc_unit_no") == item.data.get("mc_unit_no")) {
                labelInboundUser.visible = false;
                labelInboundTime.visible = false;
            }
        }
    }

    displayOutboundMessage(item: RepeatItem): void {
        const panelInbound = item.findComponentById("panelInbound") as Panel;
        item.remove(panelInbound);
        const panelOutbound = item.findComponentById("panelOutbound") as Panel;
        panelOutbound.visible = true;
        this.inboundDate = null;
        const sendDate = item.data.getDateTime("message_send_date");
        const nextMsg = this.repeaterChats.getComponent(this.repeaterChats.indexOf(item)+1) as RepeatItem;
        const nextDate = nextMsg?.data.get("message_type") == "O" ? nextMsg?.data.getDateTime("message_send_date") : null;

        if (this.isToday(sendDate)) {
            const labelOutboundTime = item.findComponentById("labelOutboundTime") as Label;
            this.formatDateToTime(item.data, labelOutboundTime);
        }

        if ((this.outboundDate == null || this.sameDate(this.outboundDate, sendDate) || nextDate == null)
                && !this.withinOneMinute(sendDate, nextDate)) {
            this.outboundDate = sendDate;
        } else {
            if (nextMsg && (nextMsg.data.get("mc_user_id") != item.data.get("mc_user_id") ||
                            nextMsg.data.get("mc_unit_no") != item.data.get("mc_unit_no"))) {
                return;
            }
            const panelOutboundInfo = item.findComponentById("panelOutboundInfo") as Panel;
            panelOutboundInfo.visible = false;
        }
    }

    withinOneMinute(date1: Date, date2: Date): boolean {
        return Math.abs(DateUtil.dateDiff(DatePart.MINUTE, date1, date2)) < 1;
    }

    sameDate(date1: Date, date2: Date): boolean {
        return DateUtil.datesEqual(DateUtil.justDate(date1), DateUtil.justDate(date2));
    }

    isToday(date: Date): boolean {
        return DateUtil.datesEqual(DateUtil.justDate(date), DateUtil.justDate(new Date()));
    }

    formatDateToTime(data: ModelRow, label: Label): void {
        label.displayType = DisplayType.TIME;
        label.displayData(data, null, 0);
    }

    labelOutboundUserOnDataDisplay(event: DataDisplayEvent) {
        const label = event.target as Label;
        if (event.rowData.isNull("mc_user_full_name")) {
            label.text = event.rowData.get("mc_user_id");
        }
    }

    setStatusIcon(statusImage: Image, status: string) {
        let imageName: ImageName;
        let tooltip: string;
        switch (status) {
            case "A": {
                imageName = ImageName.loadAvailable;
                tooltip = "Available";
                break;
            }
            case "C": {
                imageName = ImageName.loadCovered;
                tooltip = "Covered";
                break;
            }
            case "P": {
                imageName = ImageName.loadInProgress;
                tooltip = "In Progress";
                break;
            }
            case "V": {
                imageName = ImageName.circleX;
                tooltip = "Void";
                break;
            }
            case "D": {
                imageName = ImageName.loadDelivery;
                tooltip = "Delivered";
                break;
            }
        }
        statusImage.name = imageName;
        statusImage.tooltip = tooltip;
    }

    updateDisplayedName(inboundUser: Label, mcUnitNo: string) {
        const tractorMcUnit = this.sourceMcMessage.summaryData?.get("tractor_mc_unit_no", null);
        if (tractorMcUnit == mcUnitNo) {
            inboundUser.text = mcUnitNo;
        }
    }

    /* Search Logic Start */
    getHighlightText(row: ModelRow): string {
        return row.get("mc_form_number") == "0" ? row.get("inbound_message_text", row.get("outbound_message_text")) : row.get("title")
    }

    getElementForHighlight(repeatItem: RepeatItem): HTMLElement {
        if (repeatItem.components.length == 0) return;
        const visiblePanel = this.getVisiblePanel(repeatItem.components[0] as Panel);
        if (visiblePanel != null) {
            const messageType = this.getHighlightMessageType(visiblePanel.id);
            const label = this.getHighlightLabel(messageType, visiblePanel);
            if (!label?.isEmpty()) {
                return label.element;
            }
        }
        return null;
    }

    getHighlightMessageType(panelId: string) {
        if (panelId.includes("bound")) {
            return panelId == "panelOutbound" ? "O" : "I";
        } else if (panelId.includes("Form")) {
            return "form";
        }
        return null;
    }

    getHighlightLabel(messageType: string, panel: Panel) {
        if (messageType == "form") {
            return panel.findComponentById("labelForm") as Label;
        } else {
            return panel.findComponentById(`label${messageType == "O" ? "Outbound" : "Inbound"}Text`) as Label;
        }
    }

    getVisiblePanel(panel: Panel): Panel {
        return panel.components.find(c => c.visible && (c.id.includes("bound") || c.id.includes("Form"))) as Panel;
    }
    /* Search Logic End */
}
